"use client";
import { Api } from "@/Apis";
import { CustomerCenterUserSelectApiModel } from "@/Apis/ClinicApi";
import useCustomerCenterUserModal from "@/Areas/Customer/Pages/CustomerCenterUser/useCustomerCenterUserModal";
import { CommonField, If } from "@/Components";
import { Helper } from "@/Helpers";
import { useApp, useImageFallback, useT } from "@/Hooks";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import { CheckCard } from "@ant-design/pro-components";
import {
  Avatar,
  Button,
  Divider,
  Flex,
  Space,
  Spin,
  Statistic,
  Typography,
  theme,
} from "antd";
import { Dayjs } from "dayjs";
import { FC, useState } from "react";

const { Text, Title } = Typography;

export interface LandingAppointmentSetCenterUserPageProps {
  CenterId: number;
  CenterInvoiceId: number;
  ExpirationDate: Dayjs;
  callback: (result: boolean) => void;
}

const LandingAppointmentSetCenterUserPage: FC<
  LandingAppointmentSetCenterUserPageProps
> = ({ CenterId, CenterInvoiceId, ExpirationDate, callback }) => {
  const t = useT();
  const getImageFallback = useImageFallback();
  const { token } = theme.useToken();
  const { message, modal } = useApp();
  const customerCenterUserModal = useCustomerCenterUserModal();
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number>();
  const handleSelect = (id: number) => setSelectedId(id);
  const submit = async (centerUserId: number) => {
    try {
      setLoading(true);
      const r = await Api.LandingAppointment.setCenterUser({
        CenterInvoiceId: CenterInvoiceId,
        CenterUserId: centerUserId,
      });
      if (r.Success) {
        callback(true);
      } else {
        message.errors(r.Errors);
      }
    } catch (error) {}
    setLoading(false);
  };
  return (
    <Spin spinning={loading}>
      <div style={{ direction: "rtl", textAlign: "center" }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          <Space>
            <span>
              {t(
                "landing.appointment.setCenterUser.remainingTime",
                undefined,
                "Select client",
              )}
            </span>
            <Statistic.Timer
              type="countdown"
              value={ExpirationDate as any}
              onFinish={async () => {
                modal.info({
                  icon: null,
                  title: t(
                    "landing.appointment.setCenterUser.reservationTimeExpired",
                    undefined,
                    "Select client",
                  ),
                  footer: [],
                  closable: false,
                  centered: true,
                  keyboard: false,
                  content: (
                    <Spin>
                      <div style={{ height: 100 }}></div>
                    </Spin>
                  ),
                });
                await Helper.delay(3000);
                callback(false);
              }}
            />
          </Space>
        </Title>
        <Text type="secondary">
          {t(
            "landing.appointment.setCenterUser.whoIsAppointmentFor",
            undefined,
            "Select client",
          )}
        </Text>

        <Divider />

        <Flex
          justify="center"
          gap={16}
          wrap
          style={{
            marginBottom: 16,
            maxHeight: "50vh",
            overflowY: "scroll",
            overflowX: "clip",
          }}
        >
          <CommonField
            sourceType="Customer"
            methodName="CenterUsers"
            model={{ CenterId }}
            onOriginalOptionsChange={(options) => {
              if (options.length > 0) {
                setSelectedId(options[0].Value);
              }
            }}
            render={({ originalOptions, loading, ...rest }) => {
              const options = originalOptions.map(
                (option) => option as CustomerCenterUserSelectApiModel,
              );

              return (
                // <JSONPretty data={originalOptions} />
                <CheckCard.Group
                  value={selectedId}
                  onChange={handleSelect as any}
                >
                  {options.map((u, i) => (
                    <CheckCard
                      key={u.Value}
                      value={u.Value}
                      avatar={
                        <Avatar
                          size={48}
                          src={getImageFallback({
                            imageUrl: u.ImageUrl,
                            entity: "CenterUser",
                          })}
                          icon={<UserOutlined />}
                          style={{ backgroundColor: token.colorFill }}
                        />
                      }
                      title={
                        <Text strong>
                          {u.Text}
                          {i == 0 &&
                            t(
                              "landing.appointment.setCenterUser.self",
                              undefined,
                              "Select client",
                            )}
                        </Text>
                      }
                      description={
                        <Space wrap>
                          <If condition={u.Data?.PhoneNumber}>
                            <span>
                              {t(
                                "common.label.mobilePhone",
                                undefined,
                                "Select client",
                              )}
                              {u.Data?.PhoneNumber}
                            </span>
                          </If>
                          <If condition={u.Data?.NationalCode}>
                            <span>
                              {t(
                                "common.label.nationalCode",
                                undefined,
                                "Select client",
                              )}
                              {u.Data?.NationalCode}
                            </span>
                          </If>
                        </Space>
                      }
                      style={{
                        // width: 200,
                        width: "calc(100% - 5px)",
                        height: "auto",
                        textAlign: "center",
                        borderColor:
                          selectedId === u.Value
                            ? token.colorPrimary
                            : token.colorBorderSecondary,
                      }}
                    />
                  ))}
                </CheckCard.Group>
              );
            }}
          />
        </Flex>

        <Divider style={{ margin: "8px 0" }} />

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          size="large"
          block
          onClick={async () => {
            const r = await customerCenterUserModal.showCreate({ CenterId });
            if (r) await submit(r.Id);
          }}
          style={{ marginBottom: 16 }}
        >
          {t(
            "landing.appointment.setCenterUser.bookForAnotherPerson",
            undefined,
            "Select client",
          )}
        </Button>

        <Flex justify="center" gap={8}>
          <Button size="large" onClick={() => callback(false)}>
            {t("common.action.cancel", undefined, "Select client")}
          </Button>
          <Button
            type="primary"
            disabled={!selectedId}
            size="large"
            onClick={() => submit(selectedId!)}
          >
            {t("common.action.continue", undefined, "Select client")}
          </Button>
        </Flex>
      </div>
    </Spin>
  );
};

export default LandingAppointmentSetCenterUserPage;
